import React from "react";
import { Column, Heading, Row, Section, TruncatedText } from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import {
	AddressComponents,
	ButtonLink,
	EmailAddressComponents,
	GuardianComponents,
	GuardianFormat,
	GuardianUtilities,
	Icon,
	PersonFilterInput,
	PhoneNumberComponents,
	RelationshipFormat,
	useReEnrollmentGuardianDetailQuery,
} from "@hex-insights/verita-romania.shared";
import { ReEnrollmentLayout } from "../../Components";
import { reEnrollmentGuardiansIndexPageInfo } from "../IndexPage/pageinfo";
import { reEnrollmentGuardiansDetailPageInfo } from "./pageinfo";
import generalStyles from "../../general.module.css";
import styles from "./styles.module.css";

export function ReEnrollmentGuardiansDetailPage() {
	const { guardianID } = reEnrollmentGuardiansDetailPageInfo.useParams();

	const { data } = useReEnrollmentGuardianDetailQuery({ variables: { id: guardianID } });
	const guardian = data?.guardian;

	useActivePageRegistration(
		reEnrollmentGuardiansDetailPageInfo,
		guardian ? reEnrollmentGuardiansDetailPageInfo.title(guardian) : "",
	);

	const initialPeopleFilterForAddress = React.useMemo<PersonFilterInput>(() => {
		if (!guardian) {
			return { idEQ: "0" };
		}

		return {
			or: [
				{
					idEQ: guardian.person.id,
				},
				{
					guardian: [GuardianUtilities.getRelatedGuardiansFilterInput(guardian.id)],
				},
			],
		};
	}, [guardian]);

	return (
		<ReEnrollmentLayout
			footerContent={
				<Row justify="space-between" align="center">
					<ButtonLink variant="link" to={reEnrollmentGuardiansIndexPageInfo.to}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.ArrowLeft size={24} /> Back
						</Row>
					</ButtonLink>

					<ButtonLink variant="primary" to={reEnrollmentGuardiansIndexPageInfo.to}>
						Save
					</ButtonLink>
				</Row>
			}
		>
			<div style={{ width: "fit-content", margin: "0 auto" }}>
				<Heading.H1 className={generalStyles["heading"]}>Guardian Contact Information</Heading.H1>

				{guardian && (
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ minWidth: 0 }}>
							<GuardianComponents.GuardianIcon guardian={guardian} size={80} />

							<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ minWidth: 0 }}>
								<TruncatedText className={styles["guardian-name"]} style={{ minWidth: 0 }}>
									{GuardianFormat.name(guardian)}
								</TruncatedText>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
									<Icon.PersonArmsSpread size={18} color="var(--neutral-colors--tertiary)" />
									<TruncatedText className={styles["guardian-relationship-type"]}>
										{RelationshipFormat.condensedRelationshipType(guardian.relationships)}
									</TruncatedText>
								</Row>
							</Column>
						</Row>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Phone Numbers</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<PhoneNumberComponents.PhoneNumberEditablePersonList
										person={guardian.person}
										phoneNumbers={guardian.person.phoneNumbers}
										noUtilities
									/>
								</div>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Email Addresses</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<EmailAddressComponents.EmailAddressEditablePersonList
										person={guardian.person}
										emailAddresses={guardian.person.emailAddresses}
										noUtilities
									/>
								</div>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Addresses</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<AddressComponents.AddressEditableList
										addresses={guardian.person.addresses}
										initialCreatePeopleFilter={initialPeopleFilterForAddress}
										hidePeople
									/>
								</div>
							</Section.Body>
						</Section>
					</Column>
				)}
			</div>
		</ReEnrollmentLayout>
	);
}
